import React, { useState, useRef, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { SIGNUP } from '../utils/mutations';
import Auth from '../utils/auth';
import DemoButton from './DemoButton';
import { quantum } from 'ldrs';
import '../App.css';

quantum.register();

/**
 * Button system: neutral, modern, accessible
 * - Primary: dark neutral (no loud color)
 * - Secondary: light neutral
 * - Ghost: subtle outline, low emphasis
 * - Focus rings + disabled styling + active press
 */
const Btn = ({
  children,
  variant = 'primary',
  className = '',
  ...props
}) => {
  const base =
    'inline-flex items-center justify-center gap-2 select-none ' +
    'px-6 py-3 rounded-full font-semibold ' +
    'transition-all duration-200 ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ' +
    'focus-visible:ring-offset-transparent ' +
    'disabled:opacity-60 disabled:cursor-not-allowed ' +
    'active:translate-y-[1px]';

  const variants = {
    // Neutral “primary” (charcoal)
    primary:
      'bg-zinc-900 text-white ' +
      'hover:bg-zinc-800 ' +
      'focus-visible:ring-zinc-400 ' +
      'shadow-sm hover:shadow ' +
      'border border-zinc-800',

    // Neutral “secondary” (light surface)
    secondary:
      'bg-white text-zinc-900 ' +
      'hover:bg-zinc-50 ' +
      'focus-visible:ring-zinc-300 ' +
      'shadow-sm hover:shadow ' +
      'border border-zinc-200',

    // Very low emphasis, still clickable
    ghost:
      'bg-transparent text-zinc-900 ' +
      'hover:bg-zinc-100/70 ' +
      'focus-visible:ring-zinc-300 ' +
      'border border-zinc-300/80',

    // Optional: link-like but still button-ish
    subtle:
      'bg-zinc-100/70 text-zinc-900 ' +
      'hover:bg-zinc-200/70 ' +
      'focus-visible:ring-zinc-300 ' +
      'border border-zinc-200',
  };

  return (
    <a className={`${base} ${variants[variant]} ${className}`} {...props}>
      {children}
      {/* micro affordance */}
      <span aria-hidden="true" className="opacity-70 text-[0.95em]">
        →
      </span>
    </a>
  );
};

const Onboard = (props) => {
  const [loading, setLoading] = useState(false);

  function generateRandomBase64String(length) {
    var characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
    var result = '';
    for (var i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
  }

  // Generate a random base 64 32 character string
  var randomBase64StringUser = generateRandomBase64String(16);
  var randomBase64StringPassword = generateRandomBase64String(16);

  const [formState, setFormState] = useState({
    username: randomBase64StringUser,
    password: randomBase64StringPassword,
  });
  const [signUp, { error, data }] = useMutation(SIGNUP);

  // update state based on form input changes
  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormState({
      ...formState,
      [name]: value,
    });
  };

  // submit form
  const handleFormSubmit = async (event) => {
    setLoading(true);
    event.preventDefault();
    console.log(formState);
    console.log(event);
    try {
      const { data } = await signUp({
        variables: { ...formState },
      });
      console.log(data);
      console.log(formState);
      if (data.signUp.token !== null) {
        await Auth.login(data.signUp.token);
        props.setIsRegisterPaneOpen(false);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const DynamicText = () => {
    const messages = [
      'Corruption. Called out.',
      'Pollution. Reported fast.',
      'Potholes. Fixed sooner.',
      'Unfair laws. Challenged.',
      'Forests. Defended.',
      'Abuse of power. Exposed.',
      'Inequality. Measured.',
      'Climate delays. Pressured.',
      'School cuts. Tracked.',
      'Healthcare gaps. Flagged.',
      'Housing bias. Documented.',
      'Censorship. Resisted.',
      'Monopolies. Investigated.',
      'Gun violence. Mapped.',
      'Racism. Confronted.',
      'Voter suppression. Stopped.',
      'Privacy invasions. Blocked.',
      'Land theft. Opposed.',
      'Workplace abuse. Reported.',
      'Resources. Protected.',
      'Broken services. Escalated.',
      'Hate crimes. Logged.',
      'Disaster failures. Audited.',
      'Pay gaps. Proven.',
      'Rights. Reinforced.',
    ];

    const [currentMessage, setCurrentMessage] = useState(messages[0]);
    const [opacity, setOpacity] = useState(1); // kept for compatibility; not required for simple swaps

    useEffect(() => {
      const intervalId = setInterval(() => {
        setCurrentMessage(() => {
          const randomIndex = Math.floor(Math.random() * messages.length);
          return messages[randomIndex];
        });
      }, 5000);

      return () => clearInterval(intervalId);
    }, []);

    return (
      <div
        className="text-md md:text-md dynamic-text font-bold text-transparent bg-gradient-to-r from-zinc-200 to-zinc-400"
        style={{ opacity }}
      >
        {currentMessage}
      </div>
    );
  };

  return (
    <main style={{ zIndex: '5' }} className="m-5 text-white margin-auto flex flex-col">
      <div className="flex flex-col">
        <div className="flex flex-col">
          <img className="h-20 w-auto" src="logo.svg" alt="" />
          <div className="flex flex-col text-center mx-auto">
            <div className="min-h-screen">
              {/* Hero Section */}
              <section className="hero py-5">
                <div className="container mx-auto px-4 text-center">
                  <h1 className="text-2xl md:text-2xl font-bold mb-4 welcome">
                    ProjectCivilian.org
                  </h1>

                  <DynamicText />

                  <p className="text-md md:text-md mb-4">
                    Turn problems into action—fast.
                  </p>
                  <p className="text-md md:text-md mb-8">
                    Our AI helps you describe the issue, clarify the facts, and draft a clear report to the right people.
                  </p>

                  {/* Replaced old <a className="bg-white ..."> */}
                  <Btn
                    href="#about"
                    variant="secondary"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById('use-case')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    See How It Works
                  </Btn>

                  <div className="h-6" />

                  <DemoButton
                    setIsDemoPaneOpen={props.setIsDemoPaneOpen}
                    setIsOnboardOpen={props.setIsOnboardOpen}
                  />
                </div>
              </section>

              {/* Use Case Section */}
              <section id="use-case" className="use-case py-3 bg-gray-200">
                <div className="container mx-auto px-4">
                  <h2 className="text-2xl md:text-2x font-bold text-center mb-3 text-grey-800">
                    What It Does
                  </h2>
                  <div className="text-center mb-3">
                    <p className="mt-4">
                      Post an issue. We analyze it, suggest next steps, and help you send a clean, constructive message to local officials—so it’s easier to get results.
                    </p>
                  </div>

                  <DemoButton
                    setIsDemoPaneOpen={props.setIsDemoPaneOpen}
                    setIsOnboardOpen={props.setIsOnboardOpen}
                  />

                  <div className="h-6" />

                  {/* Replaced old "Show me first" */}
                  <Btn
                    href="#explore"
                    variant="ghost"
                    onClick={(e) => {
                      e.preventDefault();
                      props.setIsOnboardOpen(false);
                      props.setIntroSnackbarOpen(true);
                    }}
                  >
                    Show me first
                  </Btn>

                  <div className="h-4" />

                  {/* Replaced old "I’m in — sign me up" */}
                  <Btn
                    href="#about"
                    variant="primary"
                    className="clear-both"
                    onClick={(e) => {
                      e.preventDefault();
                      props.setIsRegisterPaneOpen(true);
                      props.setIsOnboardOpen(false);
                    }}
                  >
                    I’m in — sign me up
                  </Btn>

                  {/* Optional: if you want spacing consistent without <br/> */}
                  <div className="h-6" />
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Onboard;
